import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Sparkles, Copy, Save, CopyCheck, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { FormField } from '@/components/ui/form-field';
import { NativeSelect } from '@/components/ui/native-select';
import { ernaehrungAPI } from '@/lib/api';
import { MAHLZEIT, AUSSCHLUSS_NAME, WOCHENTAGE, MakroKacheln, fehlertext } from '@/pages/ErnaehrungPage';
import { Preis, preisText } from '@/components/ErnaehrungPreis';

/* Plan-Vorlage bearbeiten (10.10.2026): Rezept je Wochentag und Mahlzeit, Uhrzeiten, „Tag kopieren nach …“,
   „Automatisch füllen“. Mengen stehen nicht in der Vorlage — beim Zuweisen skaliert der Plan aufs Tagesziel. */

const ZEITEN = { fruehstueck: '07:30', mittag: '12:30', snack: '16:00', abend: '19:00' };
const LEER = { name: '', beschreibung: '', typ: 'woche', slots: ['fruehstueck', 'mittag', 'abend', 'snack'], zeiten: { ...ZEITEN }, tags: '', tage: {} };
const schluessel = (typ) => (typ === 'tag' ? ['0'] : ['0', '1', '2', '3', '4', '5', '6']);

/* Richtpreis eines Tages bei Grundmengen: Summe der Rezeptpreise; unvollständig, sobald einem Rezept ein Preis fehlt. */
function preisSumme(preise) {
  if (!preise.length) return null;
  const ohne = [...new Set(preise.flatMap((p) => p.ohne || []))];
  return { euro: Math.round(preise.reduce((s, p) => s + p.euro, 0) * 100) / 100, vollstaendig: preise.every((p) => p.vollstaendig), ohne };
}

function summe(gerichte) {
  const s = { kcal: 0, eiweiss: 0, fett: 0, kh: 0 };
  gerichte.forEach((n) => { Object.keys(s).forEach((k) => { s[k] += n[k] || 0; }); });
  const energie = s.eiweiss * 4 + s.kh * 4 + s.fett * 9 || 1;
  return { ...s, anteile: { eiweiss: Math.round((s.eiweiss * 4 / energie) * 100), kh: Math.round((s.kh * 4 / energie) * 100), fett: Math.round((s.fett * 9 / energie) * 100) } };
}

export default function ErnaehrungVorlagePage() {
  const { vid } = useParams();
  const neu = vid === 'neu';
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [gespeichert, setGespeichert] = useState(null);     // Serverstand (passt_zu)
  const [rezepte, setRezepte] = useState([]);
  const [tag, setTag] = useState('0');
  const [kopieZiele, setKopieZiele] = useState([]);
  const [auto, setAuto] = useState({ kcal: '2000', passt: '' });
  const [laeuft, setLaeuft] = useState('');
  const [fehler, setFehler] = useState('');

  const laden = useCallback(async () => {
    try {
      const rz = (await ernaehrungAPI.rezepte()).data.rezepte.filter((r) => r.status !== 'vorschlag' && r.aktiv !== false);
      setRezepte(rz);
      if (neu) { setF({ ...LEER }); return; }
      const { data } = await ernaehrungAPI.vorlage(vid);
      setGespeichert(data);
      setF({ name: data.name, beschreibung: data.beschreibung || '', typ: data.typ, slots: data.slots, zeiten: { ...ZEITEN, ...data.zeiten }, tags: data.tags.join(', '), tage: data.tage });
    } catch (e) { setFehler(fehlertext(e, 'Vorlage konnte nicht geladen werden')); }
  }, [neu, vid]);
  useEffect(() => { laden(); }, [laden]);

  const rzMap = useMemo(() => Object.fromEntries(rezepte.map((r) => [r.id, r])), [rezepte]);
  const optionenFuer = useCallback((slot) => {
    const passend = rezepte.filter((r) => r.mahlzeiten.includes(slot));
    const andere = rezepte.filter((r) => !r.mahlzeiten.includes(slot));
    const opt = (r) => ({ value: r.id, label: `${r.name} · ${r.naehrwerte.kcal} kcal` });
    return [{ value: '', label: '– leer (füllt der Plan) –' }, ...passend.map(opt), ...andere.map((r) => ({ ...opt(r), label: `${opt(r).label} (sonst ${r.mahlzeiten.map((m) => MAHLZEIT[m]).join('/')})` }))];
  }, [rezepte]);

  const gerichteVon = useCallback((k) => (f ? f.slots.map((s) => rzMap[(f.tage[k] || {})[s]]).filter(Boolean).map((r) => r.naehrwerte) : []), [f, rzMap]);
  const tagSumme = useMemo(() => { const g = gerichteVon(tag); return g.length ? summe(g) : null; }, [gerichteVon, tag]);
  const preiseVon = useCallback((k) => (f ? f.slots.map((s) => rzMap[(f.tage[k] || {})[s]]?.preis).filter(Boolean) : []), [f, rzMap]);
  const tagPreis = useMemo(() => preisSumme(preiseVon(tag)), [preiseVon, tag]);
  const schnittPreis = useMemo(() => {
    if (!f) return null;
    const tage = schluessel(f.typ).map(preiseVon).filter((p) => p.length).map(preisSumme);
    const alle = preisSumme(tage);
    return alle ? { ...alle, euro: Math.round((alle.euro / tage.length) * 100) / 100 } : null;
  }, [f, preiseVon]);
  const schnitt = useMemo(() => {
    if (!f) return null;
    const tage = schluessel(f.typ).map(gerichteVon).filter((g) => g.length).map(summe);
    if (!tage.length) return null;
    return summe(tage.map((t) => Object.fromEntries(['kcal', 'eiweiss', 'fett', 'kh'].map((k) => [k, t[k] / tage.length]))));
  }, [f, gerichteVon]);

  if (fehler && !f) return <div className="p-6"><Card><CardContent className="py-10 text-center text-sm text-destructive" role="alert">{fehler}</CardContent></Card></div>;
  if (!f) return <div className="p-6 max-w-5xl mx-auto space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-64 w-full" /></div>;

  const setze = (k, slot, rid) => setF({ ...f, tage: { ...f.tage, [k]: { ...(f.tage[k] || {}), [slot]: rid } } });
  const slotUmschalten = (slot, an) => setF({ ...f, slots: Object.keys(MAHLZEIT).filter((s) => (s === slot ? an : f.slots.includes(s))) });
  const kopieren = () => {
    const quelle = f.tage[tag] || {};
    const tage = { ...f.tage };
    kopieZiele.forEach((k) => { tage[k] = { ...quelle }; });
    setF({ ...f, tage });
    toast.success(`${WOCHENTAGE[Number(tag)]} kopiert nach ${kopieZiele.map((k) => WOCHENTAGE[Number(k)]).join(', ')}`);
    setKopieZiele([]);
  };
  const automatisch = async () => {
    const belegt = schluessel(f.typ).some((k) => Object.values(f.tage[k] || {}).some(Boolean));
    if (belegt && !window.confirm('Die eingetragenen Rezepte werden ersetzt. Weiter?')) return;
    setLaeuft('auto');
    try {
      const { data } = await ernaehrungAPI.vorlageAuto({ kcal: Number(auto.kcal), ausschluesse: auto.passt ? [auto.passt] : [], slots: f.slots, typ: f.typ });
      setF({ ...f, tage: data.tage });
      toast.success('Vorlage gefüllt — anpassen und speichern');
    } catch (e) { toast.error(fehlertext(e)); } finally { setLaeuft(''); }
  };
  // E4: Ein Setup (Makro-Split, Mahlzeiten mit Anteilen) aus „Plan als Vorlage“ bleibt beim Speichern erhalten, solange
  // die Mahlzeiten dieselben sind — ändert der Trainer die Mahlzeiten hier, fällt es weg (die Anteile passten nicht mehr)
  const setupPasst = !!gespeichert?.setup && JSON.stringify([...(gespeichert.setup.mahlzeiten || []).map((m) => m.slot)].sort()) === JSON.stringify([...(f?.slots || [])].sort());
  const daten = () => ({
    name: f.name.trim(), beschreibung: f.beschreibung, typ: f.typ, slots: f.slots,
    zeiten: Object.fromEntries(f.slots.map((s) => [s, f.zeiten[s] || ZEITEN[s]])),
    tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean),
    tage: Object.fromEntries(schluessel(f.typ).map((k) => [k, Object.fromEntries(f.slots.map((s) => [s, (f.tage[k] || {})[s] || null]).filter(([, r]) => r))])),
    setup: setupPasst ? { split_id: gespeichert.setup.split_id || null, split: gespeichert.setup.split || null, mahlzeiten: gespeichert.setup.mahlzeiten } : null,
  });
  const speichern = async (e) => {
    e?.preventDefault();
    setLaeuft('speichern');
    try {
      const { data } = neu ? await ernaehrungAPI.vorlageNeu(daten()) : await ernaehrungAPI.vorlageAendern(vid, daten());
      setGespeichert(data);
      toast.success('Vorlage gespeichert');
      if (neu) navigate(`/ernaehrung/vorlagen/${data.id}`, { replace: true });
    } catch (err) { toast.error(fehlertext(err)); } finally { setLaeuft(''); }
  };
  const duplizieren = async () => {
    try { const { data } = await ernaehrungAPI.vorlageDuplizieren(vid); toast.success('Kopie angelegt'); navigate(`/ernaehrung/vorlagen/${data.id}`); }
    catch (e) { toast.error(fehlertext(e)); }
  };
  const aktiverTag = f.typ === 'tag' ? '0' : tag;

  return (
    <form onSubmit={speichern} className="p-4 sm:p-6 pb-0 sm:pb-0 max-w-5xl mx-auto space-y-4">
      <Link to="/ernaehrung?reiter=vorlagen" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4 mr-1" />Vorlagen</Link>
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-semibold min-w-0 break-words">{neu ? 'Neue Vorlage' : f.name || 'Vorlage'}</h1>
        {gespeichert?.passt_zu?.filter((a) => ['vegetarisch', 'vegan', 'laktosefrei', 'glutenfrei'].includes(a)).map((a) => <Badge key={a} variant="outline" className="font-normal bg-emerald-500/10 text-emerald-700 border-emerald-200">{AUSSCHLUSS_NAME[a]}</Badge>)}
        {gespeichert?.setup?.split && (
          <Badge variant="outline" className="font-normal" title={setupPasst ? 'Beim Anwenden flext die Vorlage auf das Tagesziel des Kunden — Split und Anteile bleiben' : 'Die Mahlzeiten wurden geändert — das Setup fällt beim Speichern weg'}>
            Setup: P {gespeichert.setup.split.eiweiss_pct} / K {gespeichert.setup.split.kh_pct} / F {gespeichert.setup.split.fett_pct} %{setupPasst ? '' : ' (entfällt)'}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_20rem] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Grundinformationen</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <FormField label="Name"><Input required minLength={2} maxLength={80} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="z. B. Einsteigerplan" /></FormField>
              <FormField label="Beschreibung (optional)"><Input maxLength={500} value={f.beschreibung} onChange={(e) => setF({ ...f, beschreibung: e.target.value })} placeholder="Für wen ist die Vorlage gedacht?" /></FormField>
              <FormField label="Stichworte (mit Komma trennen)"><Input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} placeholder="Meal-Prep, Schnell & einfach" /></FormField>
              <fieldset>
                <legend className="text-xs font-medium mb-1">Aufbau</legend>
                <div role="radiogroup" className="inline-flex rounded-md border p-0.5">
                  {[['woche', 'Woche (je Wochentag)'], ['tag', 'Jeden Tag gleich']].map(([k, label]) => (
                    <button key={k} type="button" role="radio" aria-checked={f.typ === k} onClick={() => { setF({ ...f, typ: k }); setTag('0'); }}
                      className={`rounded px-3 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${f.typ === k ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}>{label}</button>
                  ))}
                </div>
              </fieldset>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Mahlzeiten und Uhrzeiten</CardTitle></CardHeader>
            <CardContent>
              <ul className="grid sm:grid-cols-2 gap-2">
                {Object.entries(MAHLZEIT).map(([slot, label]) => {
                  const an = f.slots.includes(slot);
                  return (
                    <li key={slot} className={`flex items-center gap-2 rounded-md border px-3 py-2 ${an ? '' : 'opacity-60'}`}>
                      <label className="flex items-center gap-2 text-sm flex-1"><input type="checkbox" checked={an} disabled={an && f.slots.length === 1} onChange={(e) => slotUmschalten(slot, e.target.checked)} />{label}</label>
                      <Input type="time" aria-label={`Uhrzeit ${label}`} className="w-28" disabled={!an} value={f.zeiten[slot] || ''} onChange={(e) => setF({ ...f, zeiten: { ...f.zeiten, [slot]: e.target.value } })} />
                    </li>
                  );
                })}
              </ul>
              <p className="text-xs text-muted-foreground mt-2">Ohne Snack o. Ä. verteilt sich dessen Anteil auf die übrigen Mahlzeiten.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{f.typ === 'tag' ? 'Mahlzeiten des Tages' : `${['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'][Number(aktiverTag)]} bearbeiten`}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {f.typ === 'woche' && (
                <div role="tablist" aria-label="Wochentag" className="grid grid-cols-7 gap-1">
                  {WOCHENTAGE.map((w, i) => {
                    const k = String(i);
                    const n = f.slots.filter((s) => (f.tage[k] || {})[s]).length;
                    return (
                      <button key={w} type="button" role="tab" aria-selected={tag === k} onClick={() => setTag(k)}
                        className={`rounded-md border py-1.5 text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${tag === k ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'}`}>
                        <span className="block text-sm font-medium">{w}</span>
                        <span className={`block text-[11px] tabular-nums ${tag === k ? 'opacity-90' : 'text-muted-foreground'}`}>{n}/{f.slots.length}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <ul className="space-y-2">
                {f.slots.map((slot) => {
                  const r = rzMap[(f.tage[aktiverTag] || {})[slot]];
                  return (
                    <li key={slot} className="rounded-md border p-3 space-y-2">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" aria-hidden="true" />{f.zeiten[slot]} · {MAHLZEIT[slot]}</p>
                      <NativeSelect aria-label={`Rezept ${MAHLZEIT[slot]}`} value={(f.tage[aktiverTag] || {})[slot] || ''} onChange={(v) => setze(aktiverTag, slot, v)} options={optionenFuer(slot)} />
                      {r && <p className="text-xs text-muted-foreground tabular-nums">{r.minuten ? `${r.minuten} Min. · ` : ''}{r.naehrwerte.kcal} kcal · E {r.naehrwerte.eiweiss} · KH {r.naehrwerte.kh} · F {r.naehrwerte.fett} g{preisText(r.preis) ? ` · ${preisText(r.preis)}` : ''} (Grundmenge)</p>}
                      {r?.preis && !r.preis.vollstaendig && <p className="text-xs"><Preis p={r.preis} /></p>}
                    </li>
                  );
                })}
              </ul>
              {f.typ === 'woche' && (
                <div className="rounded-md bg-muted/50 p-3 space-y-2">
                  <p className="text-sm font-medium flex items-center gap-1"><CopyCheck className="w-4 h-4" aria-hidden="true" />{WOCHENTAGE[Number(tag)]} kopieren nach …</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {WOCHENTAGE.map((w, i) => String(i) !== tag && (
                      <label key={w} className="flex items-center gap-1.5 text-sm">
                        <input type="checkbox" checked={kopieZiele.includes(String(i))} onChange={(e) => setKopieZiele(e.target.checked ? [...kopieZiele, String(i)] : kopieZiele.filter((k) => k !== String(i)))} />{w}
                      </label>
                    ))}
                    <button type="button" className="text-sm underline-offset-2 hover:underline text-primary" onClick={() => setKopieZiele(['0', '1', '2', '3', '4', '5', '6'].filter((k) => k !== tag))}>alle</button>
                  </div>
                  <Button type="button" size="sm" variant="outline" disabled={!kopieZiele.length} onClick={kopieren}>Übernehmen</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4 min-w-0 lg:sticky lg:top-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Nährwerte</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {f.typ === 'woche' && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{WOCHENTAGE[Number(tag)]}</p>
                  <MakroKacheln n={tagSumme} klein />
                  {tagPreis && <p className="text-xs mt-1"><Preis p={tagPreis} davor="Lebensmittel: " /></p>}
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground mb-1">{f.typ === 'woche' ? 'Durchschnitt pro Tag' : 'pro Tag'}</p>
                <MakroKacheln n={schnitt} klein />
                {schnittPreis && <p className="text-xs mt-1"><Preis p={schnittPreis} davor="Lebensmittel: " danach={f.typ === 'woche' ? ' im Schnitt' : ''} /></p>}
              </div>
              <p className="text-xs text-muted-foreground">Bei Grundmengen der Rezepte. Beim Zuweisen wird jedes Rezept auf das Tagesziel des Mitglieds skaliert; die Anteile bleiben ungefähr, der Preis wächst mit der Menge.</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4" aria-hidden="true" />Automatisch füllen</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <FormField label="kcal am Tag"><Input type="number" min="1000" max="6000" value={auto.kcal} onChange={(e) => setAuto({ ...auto, kcal: e.target.value })} /></FormField>
                <FormField label="passt zu"><NativeSelect value={auto.passt} onChange={(v) => setAuto({ ...auto, passt: v })} options={[{ value: '', label: 'alles' }, ...Object.entries(AUSSCHLUSS_NAME).map(([value, label]) => ({ value, label }))]} /></FormField>
              </div>
              <Button type="button" variant="outline" className="w-full" disabled={!!laeuft} onClick={automatisch}>{laeuft === 'auto' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Rezepte vorschlagen</Button>
              <p className="text-xs text-muted-foreground">Wählt nach Makros (25 % Eiweiß, 25 % Fett) und Abwechslung — danach frei anpassen.</p>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="px-4 sm:px-6 py-3 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => navigate('/ernaehrung?reiter=vorlagen')}>Abbrechen</Button>
          {!neu && <Button type="button" variant="outline" onClick={duplizieren}><Copy className="w-4 h-4 mr-1" />Duplizieren</Button>}
          <Button type="submit" disabled={!!laeuft || f.name.trim().length < 2}>{laeuft === 'speichern' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}{neu ? 'Vorlage anlegen' : 'Speichern'}</Button>
        </div>
      </div>
    </form>
  );
}
